import { useRef } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useTranslation } from "react-i18next";
import { InfoCard } from "src/design";
import {
  colorBorder,
  colorSuccess,
  colorTextMuted,
  radiusButton,
  radiusCard,
} from "src/design/tokens";
import { GalleryItem } from "../types";
import { fileToDataUrl } from "../utils/imageUtils";

interface Props {
  cover: GalleryItem | null;
  isEditing: boolean;
  onReplace: (item: GalleryItem) => void;
}

export const CoverPhotoSection = ({ cover, isEditing, onReplace }: Props) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    const data_url = await fileToDataUrl(file);
    onReplace({ file, data_url, isPending: true });
  };

  const coverSrc = cover?.url ?? cover?.data_url;

  return (
    <InfoCard
      title={t("gallery.cover.title")}
      subtitle={t("gallery.cover.subtitle")}
      icon={<ImageOutlinedIcon fontSize="small" />}
      action={
        cover && !cover.isPending ? (
          <Chip
            label={t("gallery.cover.currentlyDisplayed")}
            size="small"
            sx={{
              backgroundColor: "#f0fdf4",
              color: colorSuccess,
              border: `1px solid #b9f8cf`,
              fontWeight: 600,
              fontSize: "0.7rem",
            }}
          />
        ) : undefined
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files?.[0]) {
            handleFile(e.target.files[0]);
            e.target.value = "";
          }
        }}
      />

      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Cover photo preview */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {coverSrc ? (
            <Box
              sx={{
                margin: "auto",
                width: "50%",
                aspectRatio: "51/50",
                borderRadius: radiusCard,
                overflow: "hidden",
                border: `1px solid ${colorBorder}`,
                position: "relative",
              }}
            >
              <img
                src={coverSrc}
                alt="cover"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              {cover?.isPending && (
                <Box sx={{ position: "absolute", top: 8, left: 8 }}>
                  <Chip
                    label={t("gallery.new")}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(26,188,156,0.9)",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                    }}
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Box
              onClick={isEditing ? () => inputRef.current?.click() : undefined}
              sx={{
                width: "100%",
                aspectRatio: "51/50",
                borderRadius: radiusCard,
                border: `2px dashed ${colorBorder}`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                cursor: isEditing ? "pointer" : "default",
                "&:hover": isEditing
                  ? { borderColor: "#9CA3AF", backgroundColor: "#F9FAFB" }
                  : undefined,
              }}
            >
              <FileUploadOutlinedIcon
                sx={{ fontSize: 32, color: colorTextMuted }}
              />
              <Typography variant="body2" sx={{ color: colorTextMuted }}>
                {isEditing
                  ? t("gallery.cover.addCover")
                  : t("gallery.noPhotos")}
              </Typography>
            </Box>
          )}

          {isEditing && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileUploadOutlinedIcon />}
              onClick={() => inputRef.current?.click()}
              sx={{
                textTransform: "none",
                borderRadius: radiusButton,
                mt: 1.5,
              }}
            >
              {coverSrc
                ? t("gallery.cover.replaceCover")
                : t("gallery.cover.addCover")}
            </Button>
          )}
        </Box>
      </Box>
    </InfoCard>
  );
};
